// 导入必要的依赖
import { ref } from 'vue';
import type { Card } from './cardAnimation';

// 获取国际化函数
const getNuxtApp = () => {
  try {
    return useNuxtApp();
  } catch {
    return null;
  }
};

/**
 * 将牌型文本反向转换为牌型编号（支持中英文）
 * @param handType 牌型文本
 * @returns 牌型编号 (0-13)，未找到返回 -1
 */
const getNiuTypeFromHandType = (handType: string): number => {
  if (!handType) return -1;
  
  const nuxtApp = getNuxtApp();
  if (!nuxtApp?.$i18n) return -1;
  
  const { t } = nuxtApp.$i18n;
  
  // 遍历所有可能的牌型编号，找到匹配的
  for (let i = 0; i <= 13; i++) {
    if (t(`niuniu.niuType${i}`) === handType) {
      return i;
    }
  }
  
  return -1;
};

/**
 * 将 NiuType 数值转换为牌型名称（支持中英文）
 * @param niuType 牌型数值（0-13）
 * @returns 牌型名称
 */
export const convertNiuTypeToHandType = (niuType: number): string => {
  const nuxtApp = getNuxtApp();
  if (!nuxtApp?.$i18n) return '';
  
  const { t } = nuxtApp.$i18n;
  
  // 使用国际化翻译
  if (niuType >= 0 && niuType <= 13) {
    return t(`niuniu.niuType${niuType}`);
  }
};

/**
 * 检查是否为五花牛 (五张牌都是J/Q/K)
 */
const checkForFiveFlowerBull = (cards: Card[]): boolean => {
  return cards.every(card => card.rank === 'J' || card.rank === 'Q' || card.rank === 'K');
};

/**
 * 检查是否为四炸 (四张相同点数的牌)
 */
const checkForFourOfAKind = (cards: Card[]): boolean => {
  const rankCount: Record<string, number> = {};
  
  cards.forEach(card => {
    rankCount[card.rank] = (rankCount[card.rank] || 0) + 1;
  });
  
  return Object.values(rankCount).some(count => count >= 4);
};

/**
 * 检查是否为五小牛 (五张牌点数和不超过10，且单张点数不超过5)
 */
const checkForFiveSmallBull = (cards: Card[]): boolean => {
  const totalSum = cards.reduce((sum, card) => sum + card.value, 0);
  return totalSum <= 10 && cards.every(card => card.value <= 5);
};

/**
 * 获取所有可能的组合（通用工具函数）
 * @param arr 原始数组
 * @param k 要选择的元素个数
 */
export function getAllCombinations<T>(arr: T[], k: number): T[][] {
  const result: T[][] = [];
  
  function combine(start: number, current: T[]) {
    if (current.length === k) {
      result.push([...current]);
      return;
    }
    
    for (let i = start; i < arr.length; i++) {
      current.push(arr[i]);
      combine(i + 1, current);
      current.pop();
    }
  }
  
  combine(0, []);
  return result;
}

/**
 * 计算手牌类型 - 包含所有特殊牌型的牛牛规则（支持中英文）
 * @param cards 5张牌
 * @returns 牌型名称（已国际化）
 */
export const calculateHandType = (cards: Card[]): string => {
  // 检查输入有效性
  if (!cards || cards.length < 5) return '';

  // 检查特殊牌型：五小牛
  if (checkForFiveSmallBull(cards)) {
    return convertNiuTypeToHandType(13);
  }

  // 检查特殊牌型：四炸
  if (checkForFourOfAKind(cards)) {
    return convertNiuTypeToHandType(12);
  }

  // 检查特殊牌型：五花牛
  if (checkForFiveFlowerBull(cards)) {
    return convertNiuTypeToHandType(11);
  }

  // 标准牛牛判断逻辑：找出3张牌，使其点数之和是10的倍数
  const combinations = getAllCombinations(cards, 3);

  for (const combination of combinations) {
    const sum = combination.reduce((total, card) => total + card.value, 0);

    if (sum % 10 === 0) {
      const remainingCards = cards.filter(card => !combination.includes(card));
      const remainingSum = remainingCards.reduce((total, card) => total + card.value, 0);
      const remainder = remainingSum % 10;
      
      return convertNiuTypeToHandType(remainder === 0 ? 10 : remainder);
    }
  }

  // 无牛
  return convertNiuTypeToHandType(0);
};

// 大厅配置类型定义（对应不同倍率的配置）
export interface HallConfig {
    minimumEntry: number  // 最小进场金额
    minBet: number        // 最小下注
    maxBet: number        // 最大下注
}

// 大厅类型定义
export interface GameHall {
    enabled: boolean
    maxBet: number
    minBet: number
    minimumEntry: number
    rakeRate: number
    title: string
    name: string
    nameCn: string
    // 不同倍率的配置
    x3?: HallConfig
    x5?: HallConfig
    x10?: HallConfig
    // 索引签名，支持动态倍率
    [key: string]: any
}

// 选项卡 - 下注倍率 类型定义
export interface BetMultiple {
    num: number;         // 显示的数字
    actived: boolean;    // 是否激活
}

// 筹码类型定义
export interface BetChip {
    name: string,        //icon name
    active: boolean,     //是否选中状态
    value: number        //筹码值
}

// 选项卡类型定义
export interface TabItem {
    name: string;      // 选项卡名称
    value: { name: string; value: any }[]; // 选项卡值列表
}

/**
 * 默认下注倍率列表
 */
export const defaultMultiples = ref<BetMultiple[]>([
    { num: 3, actived: true },
    { num: 5, actived: false },
    { num: 10, actived: false }
])

/**
 * 筹码列表
 */
export const betChips = ref<BetChip[]>([
    {
        name: 'icon-chip1',
        active: true,
        value: 1
    },
    {
        name: 'icon-chip2',
        active: false,
        value: 2
    },
    {
        name: 'icon-chip5',
        active: false,
        value: 5
    },
    {
        name: 'icon-chip10',
        active: false,
        value: 10
    },
    {
        name: 'icon-chip50',
        active: false,
        value: 50
    },
    {
        name: 'icon-chip100',
        active: false,
        value: 100
    },
    {
        name: 'icon-chip200',
        active: false,
        value: 200
    },
    {
        name: 'icon-chip500',
        active: false,
        value: 500
    },
])


/**
 * 获取牌型的样式类
 * @param handType 牌型名称
 * @returns 对应的CSS类名
 */
/**
 * 获取牌型对应的CSS类名（支持中英文）
 * @param handType 牌型名称
 * @returns 对应的CSS类名
 */
export const getBettingResultClass = (handType: string): string => {
    if (!handType) return '';

    // 将牌型文本转换为编号
    const niuType = getNiuTypeFromHandType(handType);
    
    if (niuType === -1) return ''; // 未识别的牌型

    // 根据牌型编号返回对应的CSS类名
    switch (niuType) {
        case 0:  // 无牛
            return 'betting-result0';
        case 1:  // 牛一
        case 2:  // 牛二
        case 3:  // 牛三
            return 'betting-result1';
        case 4:  // 牛四
        case 5:  // 牛五
        case 6:  // 牛六
        case 7:  // 牛七
        case 8:  // 牛八
        case 9:  // 牛九
            return 'betting-result2';
        case 10: // 牛牛
        case 11: // 五花牛
        case 12: // 四炸
        case 13: // 五小牛
            return 'betting-result3';
        default:
            return '';
    }
};

/**
 * 获取牌型倍数（支持中英文）
 * @param handType 牌型名称
 * @returns 对应的倍数
 */
export const getHandTypeMultiplier = (handType: string): number => {
    if (!handType) return 1;
    
    // 将牌型文本转换为编号
    const niuType = getNiuTypeFromHandType(handType);
    
    if (niuType === -1) return 1; // 未识别的牌型，默认返回1

    // 根据牌型编号返回对应的倍数
    const multipliers: Record<number, number> = {
        0: 1,   // 无牛
        1: 1,   // 牛一
        2: 2,   // 牛二
        3: 3,   // 牛三
        4: 4,   // 牛四
        5: 4,   // 牛五
        6: 4,   // 牛六
        7: 5,   // 牛七
        8: 5,   // 牛八
        9: 5,   // 牛九
        10: 10, // 牛牛
        11: 11, // 五花牛
        12: 12, // 四炸
        13: 13  // 五小牛
    };
    
    return multipliers[niuType] ?? 1;
};

/**
 * 格式化牛型显示文本（用于走势图等场景，去掉"牛"或"Bull"，因为被图标代替）
 * @param niuType 牛型编号 (0-13)
 * @param locale 语言环境 ('cn' 或其他)
 * @returns 格式化后的文本
 * 
 * @example
 * // 中文环境
 * formatNiuTypeForDisplay(1, 'cn')  // 返回: "一" (去掉"牛"字)
 * formatNiuTypeForDisplay(10, 'cn') // 返回: "牛" (去掉第一个"牛"字，保留第二个)
 * formatNiuTypeForDisplay(0, 'cn')  // 返回: "无" (去掉"牛"字)
 * formatNiuTypeForDisplay(11, 'cn') // 返回: "五花" (去掉"牛"字)
 * 
 * // 英文环境
 * formatNiuTypeForDisplay(1, 'en')  // 返回: "1" (去掉"Bull ")
 * formatNiuTypeForDisplay(10, 'en') // 返回: "Bull" (去掉第一个"Bull ")
 * formatNiuTypeForDisplay(0, 'en')  // 返回: "No" (去掉"Bull")
 * formatNiuTypeForDisplay(11, 'en') // 返回: "Five Picture" (保持完整，因为没有Bull)
 */
export const formatNiuTypeForDisplay = (niuType: number, locale: string = 'cn'): string => {
    const nuxtApp = getNuxtApp();
    if (!nuxtApp?.$i18n) return '';
    
    const { t } = nuxtApp.$i18n;
    const fullText = t(`niuniu.niuType${niuType}`);
    
    // 中文环境
    if (locale === 'cn') {
        // 所有带"牛"字的牌型，都去掉"牛"字（图标代替）
        // 牛一到牛九：去掉"牛"字（如 "牛一" -> "一"）
        if (niuType >= 1 && niuType <= 9) {
            return fullText.replace(/^牛/, '');
        }
        
        // 牛牛：去掉第一个"牛"字（如 "牛牛" -> "牛"）
        if (niuType === 10) {
            return fullText.replace(/^牛/, '');
        }
        
        // 无牛：去掉"牛"字（如 "无牛" -> "无"）
        if (niuType === 0) {
            return fullText.replace(/牛$/, '');
        }
        
        // 五花牛、五小牛：去掉"牛"字（如 "五花牛" -> "五花"）
        if (niuType === 11 || niuType === 13) {
            return fullText.replace(/牛$/, '');
        }
    } else {
        // 英文环境
        // Bull 1 到 Bull 9：去掉"Bull "（如 "Bull 1" -> "1"）
        if (niuType >= 1 && niuType <= 9) {
            return fullText.replace(/^Bull\s+/, '');
        }
        
        // Bull Bull：去掉第一个"Bull "（如 "Bull Bull" -> "Bull"）
        if (niuType === 10) {
            return fullText.replace(/^Bull\s+/, '');
        }
        
        // No Bull：去掉"Bull"（如 "No Bull" -> "No"）
        if (niuType === 0) {
            return fullText.replace(/\s+Bull$/, '');
        }
        
        // Five Picture, Four of a Kind, Five Small Cards：保持完整（没有Bull字样）
        if (niuType === 11 || niuType === 12 || niuType === 13) {
            return fullText;
        }
    }
    
    // 其他情况，返回完整文本
    return fullText;
};

/**
 * 根据服务器返回的数据获取牌型信息（牛几 + 倍率）
 * @param niuType 牛型编号 (0-13)
 * @param multi 服务器返回的倍率字符串（可选，如 "6"）
 * @returns 包含牌型名称和倍率的对象
 * 
 * @example
 * // 使用服务器返回的数据
 * const result = getNiuTypeInfo(3, "6");
 * // 返回: { typeName: "牛三", multiplier: 6, niuType: 3 }
 * 
 * // 不提供倍率时，使用默认计算
 * const result2 = getNiuTypeInfo(10);
 * // 返回: { typeName: "牛牛", multiplier: 10, niuType: 10 }
 */
export const getNiuTypeInfo = (banker: any) => {
    // 将 niuType 转换为牌型名称（使用 i18n 多语言）
    const { $i18n } = useNuxtApp();
    const typeName = $i18n.t(`niuniu.niuType${banker.niuType}`);
    
    return {
        typeName,      // 牌型名称（如 "牛三" 或 "Bull 3"）
        multiplier:banker.multi,    // 倍率（如 6）
        niuType:banker.niuType        // 原始牌型编号（如 3）
    };
};
